import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import ExcelJS from 'exceljs';

import { jsPDF } from "jspdf";
import "jspdf-autotable";

import { Card, CardContent } from "@mui/material";
import { Helmet } from "react-helmet-async";
import RetailerWiseProductWiseSchemeHeader from "../../RetailerProductWiseScheme/RetailerWiseProductWiseSchemeHeader2";
import RetailerWiseProductWiseSchemeService from "../../RetailerProductWiseScheme/RetailerWiseProductWiseSchemeService";
import RetailerProductWiseSchemeReportGrid from "./RetailerProductWiseSchemeReportGrid";
import RetailerWiseProductWiseSchemeFilterSearch from "./RetailerProductWiseSchemeReportSearchFilter";
import LoadingProgressBar from "../../../components/loading-progressbar/loadingprogressbar";
import PopUpHandle from "../../../commons/PopUpHandle";
import { useAppStore } from "../../../store/appStore";


export default function RetailerProductWiseSchemeReportMaster() {
    const location = useLocation();

    const getTitleFromPath = (pathname) => {
        const segments = pathname.split('/').filter(Boolean); // Remove empty segments
        const lastSegment = segments[segments.length - 1] || '';

        return lastSegment
            .replace(/-/g, ' ') // Replace hyphens with spaces
            .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize each word
    };

    const title = getTitleFromPath(location.pathname);

    const getDynamicTitle = (pathname) => {
        const lastSegment = pathname.split('/').filter(Boolean).pop() || '';

        return lastSegment
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .replace(/\bPre Approved\b/g, 'Pre-Approved');
    };

    const newTitle = getDynamicTitle(location.pathname);

    const {
        moduleName,
        setModuleName,
        destroy,
        setSearchControls,
        searchControls,
        setResponseData,
        responseData,
        setFilteredData,
        filteredData,
        isListChanged,
        setIsListChanged,
        initialize,
        filterPayload
    } = useAppStore();

    useEffect(() => {
        if (moduleName !== title) {
            destroy(); //  this resets the entire store to initial state
            initialize();
            setResponseData({});
            setFilteredData(undefined);
            setModuleName(title);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleName]);


    const [isFilterOpen, setFilterOpen] = useState(false); // Define state for filter modal
    const [loadingCount, setLoadingCount] = useState(0);
    const showLoading = () => setLoadingCount((prev) => prev + 1);
    const hideLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));


    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(0);



    const { pageType } = useParams();
    const handleOpenFilter = () => {
        setFilterOpen(true);
    };

    const handleCloseFilter = () => {
        setFilterOpen(false);
    };
    const handleClose = () => {
        setOpen(false);
    }

    const handleFilterError = ({ errorCode: code, errorMessage: message }) => {
        setErrorCode(code);
        setError(message);
        setOpen(true);
    };

    useEffect(() => {
        const fetchDynamicUISearchControls = async () => {
            showLoading();
            try {
                const schemeConversionFilterServiceInstance = await RetailerWiseProductWiseSchemeService;

                if (!schemeConversionFilterServiceInstance) {
                    throw new Error("PostedFilterService instance could not be created.");
                }

                const response = await schemeConversionFilterServiceInstance.GetRetailerWiseProductWiseSchemeReportSearchControls();
                if (response && response.data) {
                    setSearchControls(response.data);
                } else {
                    console.error("Invalid response format for GetDynamicUISearchControls:", response);
                    setSearchControls([]);
                }
            } catch (err) {
                console.error("Error fetching Dynamic UI Search Controls:", err);
                setSearchControls([]);
            } finally {
                hideLoading();
            }
        };

        if (moduleName != null && (searchControls.length === 0 || isListChanged)) {
            fetchDynamicUISearchControls();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleName, isListChanged]);

    const downloadExcel = async () => {
        if (!filteredData) return;

        // Normalize response: support both old array response and new { reportData, invoiceData } shape
        let reportRows = [];
        let invoiceRows = [];

        if (Array.isArray(filteredData) && filteredData.length > 0) {
            reportRows = filteredData;
        } else if (filteredData && typeof filteredData === 'object') {
            if (Array.isArray(filteredData.reportData) && filteredData.reportData.length > 0) {
                reportRows = filteredData.reportData;
            } else if (Array.isArray(filteredData.data) && filteredData.data.length > 0) {
                // in case response wrapped under data
                reportRows = filteredData.data;
            }

            if (Array.isArray(filteredData.invoiceData) && filteredData.invoiceData.length > 0) {
                invoiceRows = filteredData.invoiceData;
            }
        }

        if (!reportRows || reportRows.length === 0) return;

        const workbook = new ExcelJS.Workbook();

        const addSheetFromRows = (name, rows) => {
            const ws = workbook.addWorksheet(name);
            const firstRow = rows[0] || {};
            const columns = Object.keys(firstRow)
                .filter(key => key.toLowerCase() !== 'id')
                .map(key => ({ header: key.charAt(0).toUpperCase() + key.slice(1), key }));
            ws.columns = columns;

            rows.forEach(dataRow => {
                const row = {};
                Object.keys(dataRow).forEach(key => {
                    if (key.toLowerCase().includes('date') && dataRow[key]) {
                        row[key] = new Date(dataRow[key]).toLocaleDateString('en-GB');
                    } else {
                        row[key] = dataRow[key];
                    }
                });
                ws.addRow(row);
            });

            // Auto-fit column width
            ws.columns.forEach(column => {
                let maxLength = (column.header || '').length;
                column.eachCell({ includeEmpty: true }, cell => {
                    if (cell.value) {
                        const cellValue = cell.value.toString();
                        maxLength = Math.max(maxLength, cellValue.length);
                    }
                });
                column.width = maxLength + 2;
            });

            // Style header
            ws.getRow(1).eachCell(cell => {
                cell.font = { bold: true };
                cell.alignment = { vertical: 'center', horizontal: 'center' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
            });

            // Style body
            ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
                if (rowNumber !== 1) {
                    row.eachCell({ includeEmpty: true }, cell => {
                        cell.alignment = { vertical: 'center', horizontal: 'left', wrapText: true };
                        cell.border = {
                            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
                        };
                    });
                }
            });
        };

        // First sheet: existing report data
        addSheetFromRows('Summary', reportRows);

        // Second sheet: invoice details (if present)
        if (invoiceRows && invoiceRows.length > 0) {
            addSheetFromRows('Invoice Details', invoiceRows);
        }

        // Generate and download Excel (unchanged)
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const timeStamp = new Date().toISOString().replace(/[:\-T]/g, '').slice(0, 15);
        link.download = `RetailerWiseProductWiseSchemeReport_${timeStamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const exportToPDF = () => {
        if (!filteredData) return;

        // 1. Normalize the data to get the report array (same logic as downloadExcel)
        let reportRows = [];

        if (Array.isArray(filteredData) && filteredData.length > 0) {
            reportRows = filteredData;
        } else if (filteredData && typeof filteredData === 'object') {
            if (Array.isArray(filteredData.reportData) && filteredData.reportData.length > 0) {
                reportRows = filteredData.reportData;
            } else if (Array.isArray(filteredData.data) && filteredData.data.length > 0) {
                // in case response wrapped under data
                reportRows = filteredData.data;
            }
        }

        // If no valid rows are found, exit safely
        if (!reportRows || reportRows.length === 0) return;

        // 2. Use reportRows instead of filteredData
        const allKeys = Object.keys(reportRows[0]).filter(
            (key) => key.toLowerCase() !== "id"
        );

        // Keep only columns that have at least one non-empty value
        const validKeys = allKeys.filter((key) =>
            reportRows.some(
                (row) =>
                    row[key] !== null &&
                    row[key] !== undefined &&
                    row[key].toString().trim() !== ""
            )
        );

        const headers = validKeys.map(
            (key) => key.charAt(0).toUpperCase() + key.slice(1)
        );

        const rows = reportRows.map((row) =>
            validKeys.map((key) => {
                const value = row[key];
                if (value === null || value === undefined) return "";
                if (key.toLowerCase().includes("date")) {
                    return new Date(value).toLocaleDateString("en-GB"); // DD/MM/YYYY
                }
                return value.toString();
            })
        );

        // 3. Generate the PDF
        // eslint-disable-next-line new-cap
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4",
        });

        // Optional title
        doc.text("Retailer Wise Product Wise Scheme", 40, 30);

        const columnWidths = validKeys.map((key) =>
            key.toLowerCase() === "name" ? 130 : "auto"
        );

        // 4. Safely call autoTable
        // Note: If you are using a newer version of jspdf-autotable and this still crashes, 
        // change "doc.autoTable({" to "autoTable(doc, {" and import it as: import autoTable from 'jspdf-autotable';
        doc.autoTable({
            head: [headers],
            body: rows,
            styles: { cellPadding: 4, fontSize: 8 },
            headStyles: {
                fillColor: [197, 217, 241],
                textColor: 20,
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "top",
            },
            columnStyles: Object.fromEntries(
                columnWidths.map((w, i) => [i, { cellWidth: w }])
            ),
            startY: 40,
        });

        const timeStamp = new Date().toISOString().replace(/[:\-T]/g, "").slice(0, 15);
        doc.save(`RetailerWiseProductWiseSchemeReport_${timeStamp}.pdf`);
    };

    const hidePdfButton = true;
    return (
        <Card sx={{
            padding: '2px',
            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            margin: '5px 3px 5px 3px',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,

        }}>
            <CardContent style={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                paddingBottom: '0.5rem',
                minHeight: 0
            }}>
                <Helmet>
                    <title>{`${newTitle} | ProClaimz`}</title>
                </Helmet>

                
                <RetailerWiseProductWiseSchemeHeader headerName={newTitle} exportToPDF={exportToPDF} hidePdfButton={hidePdfButton} exportToExcel={downloadExcel} handleOpenFilter={handleOpenFilter} />
                <RetailerWiseProductWiseSchemeFilterSearch open={isFilterOpen} onClose={handleCloseFilter} onError={handleFilterError} />
                <RetailerProductWiseSchemeReportGrid headerName={newTitle} />
                {loadingCount > 0 && <LoadingProgressBar />}
                {open && <PopUpHandle open={open} handleClose={handleClose} error={error} errorCode={errorCode} />}

            </CardContent>
        </Card>
    )
}